import type {
  DataObjectFieldRAGRuleRaw,
  DataObjectFieldWidth,
  RelationshipType,
  RollupOperation
} from "@certa/types/src/dataObjects.types";
import type { ObjectUISchema } from "@certa/queries/types/studio.types";

export type StudioObject = {
  id: string;
  tag: string;
  name: string;
  sections: Record<string, ObjectSection>;
  fields: Record<string, ObjectField>;
  relationships: Record<string, ObjectRelationshipDef>;
  sectionSequence: string[];
  uiSchema?: ObjectUISchema;
  object: ObjectMutableProperties;
};

export type ObjectDisplayNames = ObjectUISchema["displayNames"];

export type ObjectMutableProperties = {
  displayNames: ObjectDisplayNames;
};

export type ObjectSection = {
  id: string;
  name: string;
  fields: string[];
};

export type ObjectField = {
  id: string;
  name: string;
  indexed?: boolean;
  type: StudioObjectFieldTypes;
  isRequired: boolean;
  format?: string; // TODO: Figure out the use case for format.
  width?: DataObjectFieldWidth;
  rollupConfig?: RollupDef; // rollup field configuration
  screeningConfig?: Record<string, unknown>; // Screening configuration is json
  uniqueItems?: boolean; // For array and screening fields.
  description?: string; // For help text
  rag?: DataObjectFieldRAGRuleRaw; // For UI schema rag generation
  // For array fields
  items?: {
    type: string; // For type of items inside an array field.
  };
  // relationship?: unknown;
};

export type ObjectRelationshipDef = {
  id: string;
  type: RelationshipType;
  relatedObjectTag: string;
  reverseName: string;
  relationshipLabel: string;
  // ... add other relationship related properties.
};

export type RollupDef = {
  fromObjectType: string;
  forwardRelationName: string;
  sourceFieldPath: string;
  operationType: RollupOperation;
  rollupType: "number" | "integer"; // For count -> Integer, For rest -> Decimal
};

// Keeping this here for now, as it's required in the process designer.
export type RenderableRelationships = ObjectRelationshipDef & {
  hostObjectName: string;
  relatedObjectName: string;
};

export enum ObjectFieldType {
  "TEXT" = "string",
  "BOOLEAN" = "boolean",
  "DECIMAL" = "number",
  "INTEGER" = "integer",
  "ARRAY" = "array",
  "URL" = "url",
  "EMAIL" = "email",
  "PHONE_NUMBER" = "phone_number",
  "COUNTRY" = "country",
  "FOREIGN_KEY" = "foreign_key",
  "ROLLUP" = "rollup",
  "DATE" = "date",
  "DATE_TIMEZONE_AWARE" = "datetime",
  "OWNER" = "owner",
  "SCREENING" = "screening",
  "DATA_MONITORING_IDENTIFIER" = "data_monitoring_identifier",
  "DATA_MONITORING_FIELD" = "data_monitoring_field",
  "FILE" = "file"
}

export type StudioObjectFieldTypes = Exclude<
  ObjectFieldType,
  ObjectFieldType.FOREIGN_KEY
>;
